/**
 * PanelrManager.ts - PanelrManager - Core panel management system
 * 
 * CHANGE HISTORY:
 * - 251021_0729 [dfa05af] danielbeer: fix(panelr): fix context and all tests now passing
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
/**
 * PanelrManager.ts - PanelrManager - Platform-agnostic panel management core
 * 
 * CHANGE HISTORY:
 * - 251021_0708 [b9d9c41] danielbeer: fix(panelr): fix all TypeScript errors and make production ready
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
export type PanelMode = 'invisible' | 'hidden' | 'minimized' | 'normal';

interface PanelDefaults {
  position?: 'left' | 'right' | 'top' | 'bottom';
  collapsible?: boolean;
  resizable?: boolean;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  persistent?: boolean;
  isOpen?: boolean;
  [key: string]: any;
}

interface PanelrManagerOptions {
  actr?: any;
  namespace?: string;
  defaults?: PanelDefaults;
  showBorders?: boolean;
  colorCodeLevels?: boolean;
  [key: string]: any;
}

// PanelConfig interface for future use
// interface PanelConfig {
//   id: string;
//   mode?: PanelMode;
//   position?: 'left' | 'right' | 'top' | 'bottom';
//   widths?: {
//     minimized: number;
//     normal: number;
//   };
//   heights?: {
//     minimized: number;
//     normal: number;
//   };
//   collapsible?: boolean;
//   resizable?: boolean;
//   persistent?: boolean;
//   showToggle?: boolean;
//   isOpen?: boolean;
//   width?: number;
//   height?: number;
//   [key: string]: any;
// }

/**
 * PanelrManager class
 */
export class PanelrManager {
  private actr: any;
  private panels: Map<string, any>;
  private listeners: Map<string, Function[]>;
  private state: Map<string, any>;
  private defaults: PanelDefaults;
  private namespace: string;

  private showBorders: boolean;
  private colorCodeLevels: boolean;

  constructor(options: PanelrManagerOptions = {}) {
    this.actr = options.actr || this._tryGetActr();
    this.panels = new Map(); // Active panels by ID
    this.listeners = new Map(); // Event listeners
    this.state = new Map(); // Panel states (open/closed, size, position)
    this.defaults = {
      position: 'left',
      collapsible: true,
      resizable: false,
      defaultWidth: 320,
      minWidth: 200,
      maxWidth: 600,
      persistent: true,
      isOpen: true,
      ...(options.defaults || {})
    };
    this.namespace = options.namespace || 'panelr';
    
    // Visual management options
    this.showBorders = (options.showBorders as any) ?? false;
    this.colorCodeLevels = (options.colorCodeLevels as any) ?? false;
    
    // Apply initial visual settings
    this.updateVisualSettings();
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }
  
  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      const { getActr } = require('@opcentral/actr-lib');
      return getActr();
    } catch {
      return null;
    }
  }
  
  /**
   * Register Panelr actions with Actr
   * 
   * ARCHITECTURE NOTE: Panelr uses HYBRID pattern
   * 
   * Why Hybrid:
   * 1. Panel State: Panel configurations are persisted
   * 2. Dynamic Operations: Runtime panel operations (open, close, toggle)
   * 3. State Entity Management: Panel state tracked and saved
   * 
   * Pattern: Hybrid (Actions + Selective CRUD)
   * - Actions: For panel operations (open, close, toggle)
   * - CRUD: For panel state persistence
   */
  registerActions(): void {
    if (!this.actr) return;
    
    // Panel actions
    this.actr.register('panelr:open', {
      handler: async (params: any) => {
        return this.open(params.id);
      }
    });
    
    this.actr.register('panelr:close', {
      handler: async (params: any) => {
        return this.close(params.id);
      }
    });
    
    this.actr.register('panelr:toggle', {
      handler: async (params: any) => {
        return this.toggle(params.id);
      }
    });
    
    // TODO: Selective CRUD for panel state
    // Note: registerCRUD method needs to be implemented in Actr
    /*
    this.actr.registerCRUD('panel-state', {
      create: async (data) => {
        this.state.set(data.id, data);
        this.saveState(data.id);
        return data;
      },
      
      read: async (id) => {
        return this.state.get(id);
      },
      
      update: async (id, data) => {
        const existing = this.state.get(id) || {};
        const updated = {
          ...existing,
          ...data
        };
        this.state.set(id, updated);
        this.saveState(id);
        return updated;
      },
      
      delete: async (id) => {
        const existing = this.state.get(id);
        this.state.delete(id);
        this.deleteState(id);
        return existing;
      }
    });
    */
  }

  /**
   * Register a panel
   */
  register(id: string, config = {}): any {
    const panelConfig = {
      ...this.defaults,
      ...(config as any),
      id,
      level: (config as any).level ?? 0
    };

    // Load saved state if persistent
    if (panelConfig.persistent) {
      const savedState = this.loadState(id);
      if (savedState) {
        panelConfig.isOpen = (savedState as any).isOpen ?? panelConfig.isOpen ?? true;
        // Ensure saved width respects min/max constraints
        const savedWidth = (savedState as any).width ?? panelConfig.defaultWidth;
        panelConfig.width = Math.max(
          panelConfig.minWidth,
          Math.min(panelConfig.maxWidth, savedWidth)
        );
        panelConfig.position = (savedState as any).position ?? panelConfig.position;
      }
    }

    this.panels.set(id, panelConfig);
    this.emit('panel-registered', { id, config: panelConfig });
    this.emit('state-changed', { id, state: panelConfig });

    return panelConfig;
  }

  /**
   * Unregister a panel
   */
  unregister(id: string): boolean {
    const panel = this.panels.get(id);
    if (!panel) return false;

    this.panels.delete(id);
    this.emit('panel-unregistered', { id });
    return true;
  }

  /**
   * Open a panel
   */
  open(id: string): boolean {
    const panel = this.panels.get(id);
    if (!panel) return false;

    panel.isOpen = true;
    this.saveState(id, { isOpen: true });
    this.emit('panel-opened', { id });
    this.emit('state-changed', { id, state: panel });

    return true;
  }

  /**
   * Close a panel
   */
  close(id: string): boolean {
    const panel = this.panels.get(id);
    if (!panel) return false;

    panel.isOpen = false;
    this.saveState(id, { isOpen: false });
    this.emit('panel-closed', { id });
    this.emit('state-changed', { id, state: panel });

    return true;
  }

  /**
   * Toggle panel open/closed
   */
  toggle(id: string): boolean {
    const panel = this.panels.get(id);
    if (!panel) return false;

    return panel.isOpen ? this.close(id) : this.open(id);
  }

  /**
   * Resize a panel
   */
  resize(id: string, width: number): boolean {
    const panel = this.panels.get(id);
    if (!panel || !panel.resizable) return false;

    // Clamp to min/max
    const newWidth = Math.max(
      panel.minWidth,
      Math.min(panel.maxWidth, width)
    );

    panel.width = newWidth;
    this.saveState(id, { width: newWidth });
    this.emit('panel-resized', { id, width: newWidth });
    this.emit('state-changed', { id, state: panel });

    return true;
  }

  /**
   * Set panel position
   */
  setPosition(id: string, position: 'left' | 'right' | 'top' | 'bottom'): boolean {
    const panel = this.panels.get(id);
    if (!panel) return false;

    panel.position = position;
    this.saveState(id, { position });
    this.emit('panel-position-changed', { id, position });
    this.emit('state-changed', { id, state: panel });

    return true;
  }

  /**
   * Set panel mode (invisible/hidden/minimized/normal)
   */
  setMode(id: string, mode: PanelMode): boolean {
    const panel = this.panels.get(id);
    if (!panel) {
      console.warn(`[Panelr] Panel "${id}" not found`);
      return false;
    }

    console.log(`[Panelr] Setting mode for "${id}": ${panel.mode} → ${mode}`);
    console.log(`[Panelr] Panel widths config:`, panel.widths);
    
    panel.mode = mode;
    
    // Update isOpen based on mode
    panel.isOpen = mode !== 'invisible' && mode !== 'hidden';
    
    // Update width based on mode
    if (mode === 'minimized' && panel.widths?.minimized) {
      console.log(`[Panelr] Setting width to minimized: ${panel.widths.minimized}`);
      panel.width = panel.widths.minimized;
    } else if (mode === 'normal' && panel.widths?.normal) {
      console.log(`[Panelr] Setting width to normal: ${panel.widths.normal}`);
      panel.width = panel.widths.normal;
    }
    
    console.log(`[Panelr] Panel "${id}" state after setMode:`, { mode, isOpen: panel.isOpen, width: panel.width });
    
    this.saveState(id, { mode, isOpen: panel.isOpen, width: panel.width });
    this.emit('panel-mode-changed', { id, mode });
    console.log(`[Panelr] Emitting state-changed for "${id}"`);
    this.emit('state-changed', { id, state: panel });

    return true;
  }

  /**
   * Get panel mode
   */
  getMode(id: string): PanelMode | undefined {
    const panel = this.panels.get(id);
    return panel?.mode;
  }

  /**
   * Cycle panel mode (hidden → normal → minimized → hidden)
   */
  cycleMode(id: string): PanelMode | undefined {
    const panel = this.panels.get(id);
    if (!panel) return undefined;

    const currentMode = panel.mode || 'normal';
    let nextMode: PanelMode;

    switch (currentMode) {
      case 'hidden':
        nextMode = 'normal';
        break;
      case 'normal':
        nextMode = 'minimized';
        break;
      case 'minimized':
        nextMode = 'hidden';
        break;
      case 'invisible':
        nextMode = 'normal';
        break;
      default:
        nextMode = 'normal';
    }

    this.setMode(id, nextMode);
    return nextMode;
  }

  /**
   * Calculate content area offsets based on visible panels
   */
  calculateContentArea(): { top: number; left: number; right: number; bottom: number } {
    const area = { top: 0, left: 0, right: 0, bottom: 0 };

    for (const panel of this.panels.values()) {
      // Skip if panel is not open, invisible, or hidden
      if (!panel.isOpen || panel.mode === 'invisible' || panel.mode === 'hidden') {
        console.log(`[Panelr] Skipping panel "${panel.id}" - isOpen: ${panel.isOpen}, mode: ${panel.mode}`);
        continue;
      }

      const width = panel.width || panel.defaultWidth || 0;
      const height = panel.height || panel.defaultHeight || 0;

      // console.log(`[Panelr] Including panel "${panel.id}" - position: ${panel.position}, width: ${width}, mode: ${panel.mode}`); // Disabled - too noisy

      switch (panel.position) {
        case 'left':
          area.left = Math.max(area.left, width);
          break;
        case 'right':
          area.right = Math.max(area.right, width);
          break;
        case 'top':
          area.top = Math.max(area.top, height);
          break;
        case 'bottom':
          area.bottom = Math.max(area.bottom, height);
          break;
      }
    }

    // console.log(`[Panelr] Content area calculated:`, area); // Disabled - too noisy
    return area;
  }

  /**
   * Get panel state
   * Returns a shallow copy to ensure React detects state changes
   */
  getState(id: string): any {
    const panel = this.panels.get(id);
    return panel ? { ...panel } : undefined;
  }

  /**
   * Get all panels
   */
  getAllPanels(): any {
    return Array.from(this.panels.values());
  }

  /**
   * Get panels by position
   */
  getPanelsByPosition(position: string): any {
    return Array.from(this.panels.values()).filter((p: any) => p.position === position);
  }

  /**
   * Save panel state (via Keepr if available)
   */
  saveState(id: string, state: any): void {
    const panel = this.panels.get(id);
    if (!panel || !panel.persistent) return;

    const key = `${this.namespace}.${id}`;
    const currentState = this.state.get(id) || {};
    const newState = { ...currentState, ...state };
    
    this.state.set(id, newState);

    // Save to localStorage for now (will integrate Keepr later)
    try {
      localStorage.setItem(key, JSON.stringify(newState));
    } catch (e) {
      console.warn('Failed to save panel state:', e);
    }
  }

  /**
   * Load panel state
   */
  loadState(id: string): any {
    const key = `${this.namespace}.${id}`;
    
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const state = JSON.parse(saved);
        this.state.set(id, state);
        return state;
      }
    } catch (e) {
      console.warn('Failed to load panel state:', e);
    }

    return null;
  }

  /**
   * Clear saved state
   */
  clearState(id: string): void {
    const key = `${this.namespace}.${id}`;
    this.state.delete(id);
    
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('Failed to clear panel state:', e);
    }
  }

  /**
   * Toggle panel borders visibility
   */
  setShowBorders(show: boolean): void {
    this.showBorders = show;
    this.updateVisualSettings();
    this.emit('visual-settings-changed', { showBorders: show });
  }

  /**
   * Toggle level-based color coding
   */
  setColorCodeLevels(enable: boolean): void {
    this.colorCodeLevels = enable;
    this.updateVisualSettings();
    this.emit('visual-settings-changed', { colorCodeLevels: enable });
  }

  /**
   * Update CSS variables for visual settings
   */
  private updateVisualSettings(): void {
    const root = document.documentElement;
    
    // Panel borders
    if (this.showBorders) {
      root.style.setProperty('--panelr-border-width', '1px');
      root.style.setProperty('--panelr-border-style', 'solid');
      root.style.setProperty('--panelr-border-color', 'rgba(0, 0, 0, 0.1)');
    } else {
      root.style.setProperty('--panelr-border-width', '0');
      root.style.setProperty('--panelr-border-style', 'none');
      root.style.setProperty('--panelr-border-color', 'transparent');
    }
    
    // Level colors (for color coding)
    if (this.colorCodeLevels) {
      root.style.setProperty('--panelr-level-0-color', 'rgba(59, 130, 246, 0.3)'); // Blue
      root.style.setProperty('--panelr-level-1-color', 'rgba(16, 185, 129, 0.3)'); // Green
      root.style.setProperty('--panelr-level-2-color', 'rgba(245, 158, 11, 0.3)'); // Amber
      root.style.setProperty('--panelr-level-3-color', 'rgba(239, 68, 68, 0.3)');  // Red
    } else {
      root.style.setProperty('--panelr-level-0-color', 'transparent');
      root.style.setProperty('--panelr-level-1-color', 'transparent');
      root.style.setProperty('--panelr-level-2-color', 'transparent');
      root.style.setProperty('--panelr-level-3-color', 'transparent');
    }
  }

  /**
   * Get current visual settings
   */
  getVisualSettings(): { showBorders: boolean; colorCodeLevels: boolean } {
    return {
      showBorders: this.showBorders,
      colorCodeLevels: this.colorCodeLevels
    };
  }

  /**
   * Reset panel to defaults
   */
  reset(id: string): boolean {
    const panel = this.panels.get(id);
    if (!panel) return false;

    this.clearState(id);
    
    // Reset to defaults
    (panel as any).isOpen = this.defaults.isOpen ?? true;
    panel.width = panel.defaultWidth;
    (panel as any).position = this.defaults.position ?? 'left';

    this.emit('panel-reset', { id });
    this.emit('state-changed', { id, state: panel });

    return true;
  }

  /**
   * Event system
   */
  on(event: string, callback: Function): Function {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Emit event
   */
  emit(event: string, data?: any): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(): void {
    this.listeners.clear();
  }

  /**
   * Destroy manager
   */
  destroy(): void {
    this.panels.clear();
    this.state.clear();
    this.removeAllListeners();
  }
}

// Singleton instance
let instance: PanelrManager | null = null;

/**
 * Get singleton instance
 */
export function getPanelr(options?: PanelrManagerOptions): PanelrManager {
if (!instance) {
  instance = new PanelrManager(options);
}
return instance;
}

/**
 * Reset singleton (useful for testing)
 */
export function resetPanelr() {
  if (instance) {
    instance.destroy();
  }
  instance = null;
}

export default PanelrManager;
